import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  ClaimReason,
  ClaimSort,
  ClaimStatus,
  ClaimType,
} from '@/lib/api-admin/types';

export const CLAIM_SORTABLE_COLUMNS = new Set(['requestedAt', 'completedAt', 'claimStatus']);

export const toggleClaimSort = (current: ClaimSort, columnId: string): ClaimSort => {
  if (columnId === 'requestedAt') {
    return current === 'requestedAt,desc' ? 'requestedAt,asc' : 'requestedAt,desc';
  }
  if (columnId === 'completedAt') {
    return 'completedAt,desc';
  }
  if (columnId === 'claimStatus') {
    return 'claimStatus,asc';
  }
  return current;
};

/** QA-626 검색어 유형 — ALL(통합검색)은 BE 기존 q(상품명·상품코드·자체상품코드·주문번호·주문자명 부분일치). */
export type ClaimSearchType = 'ALL' | 'PRODUCT_NAME' | 'PRODUCT_CODE' | 'GOODS_CD' | 'ORDER_NO' | 'ORDERER_NAME';

export const CLAIM_SEARCH_TYPES: ReadonlyArray<ClaimSearchType> = ['ALL', 'PRODUCT_NAME', 'PRODUCT_CODE', 'GOODS_CD', 'ORDER_NO', 'ORDERER_NAME'];

/** [검색]을 눌러야 조회에 쓰이는 조건(QA-626). */
export interface ClaimFilterInputs {
  searchType: ClaimSearchType;
  q: string;
  claimStatus: ClaimStatus | null;
  claimType: ClaimType | null;
  reasonCode: ClaimReason | null;
  /** 클레임(신청)일. */
  dateFrom: string | null;
  dateTo: string | null;
  /** 주문일. */
  orderDateFrom: string | null;
  orderDateTo: string | null;
}

export interface ClaimFiltersState {
  searchType: ClaimSearchType;
  q: string;
  orderDateFrom: string | null;
  orderDateTo: string | null;
  claimStatus: ClaimStatus | null;
  claimType: ClaimType | null;
  reasonCode: ClaimReason | null;
  dateFrom: string | null;
  dateTo: string | null;
  page: number;
  size: number;
  sort: ClaimSort;
  setQ: (q: string) => void;
  setClaimStatus: (s: ClaimStatus | null) => void;
  setClaimType: (t: ClaimType | null) => void;
  setReasonCode: (r: ClaimReason | null) => void;
  setDateFrom: (d: string | null) => void;
  setDateTo: (d: string | null) => void;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  setSort: (sort: ClaimSort) => void;
  applyFilters: (f: ClaimFilterInputs) => void;
  reset: () => void;
}

type InitialState = Omit<
  ClaimFiltersState,
  | 'setQ'
  | 'setClaimStatus'
  | 'setClaimType'
  | 'setReasonCode'
  | 'setDateFrom'
  | 'setDateTo'
  | 'setPage'
  | 'setSize'
  | 'setSort'
  | 'applyFilters'
  | 'reset'
>;

const INITIAL: InitialState = {
  searchType: 'ALL',
  q: '',
  orderDateFrom: null,
  orderDateTo: null,
  claimStatus: null,
  claimType: null,
  reasonCode: null,
  dateFrom: null,
  dateTo: null,
  page: 1,
  size: 20,
  sort: 'requestedAt,desc',
};

export const useClaimFiltersStore = create<ClaimFiltersState>()(
  devtools(
    (set) => ({
      ...INITIAL,
      setQ: (q) => set({ q, page: 1 }, false, 'setQ'),
      setClaimStatus: (claimStatus) =>
        set({ claimStatus, page: 1 }, false, 'setClaimStatus'),
      setClaimType: (claimType) => set({ claimType, page: 1 }, false, 'setClaimType'),
      setReasonCode: (reasonCode) =>
        set({ reasonCode, page: 1 }, false, 'setReasonCode'),
      setDateFrom: (dateFrom) => set({ dateFrom, page: 1 }, false, 'setDateFrom'),
      setDateTo: (dateTo) => set({ dateTo, page: 1 }, false, 'setDateTo'),
      setPage: (page) => set({ page }, false, 'setPage'),
      setSize: (size) => set({ size, page: 1 }, false, 'setSize'),
      setSort: (sort) => set({ sort, page: 1 }, false, 'setSort'),
      applyFilters: (f) => set({ ...f, page: 1 }, false, 'applyFilters'),
      reset: () => set({ ...INITIAL }, false, 'reset'),
    }),
    { name: 'claim-filters' },
  ),
);
